import { Equality, OrderEdge, Provenance, VarId } from '../types'

const EPS = 1e-9

export interface GraphConflict {
  kind: 'cycle' | 'selfLoop' | 'emptyInterval'
  provenance: Provenance[]
}

export interface GraphResult {
  ok: boolean
  conflict?: GraphConflict
  /** All node ids, greatest-first along the constraint direction (when ok). */
  order: VarId[]
  /** Final [lo, hi] per node id, aliases included (when ok). */
  intervals: Record<VarId, { lo: number; hi: number }>
  /** Does the graph entail value(gt) strictly greater than value(lt)? */
  entails(gt: VarId, lt: VarId): boolean
}

const constantValue = (id: VarId): number | undefined => {
  if (!id.startsWith('k|')) return undefined
  const value = Number(id.slice(2))
  return Number.isNaN(value) ? undefined : value
}

/**
 * Order-constraint satisfiability core: union-find over equalities, strict
 * self-loop rejection, SCC cycle detection, and interval propagation over
 * constant bounds. Sound and complete for orderings + constant bounds
 * (spec §7.1-7.2); every conflict carries the provenances that produced it.
 */
export const buildGraph = (
  edges: OrderEdge[],
  equalities: Equality[],
  seedIntervals: Record<VarId, { lo: number; hi: number }>
): GraphResult => {
  const nodes = new Set<VarId>()
  edges.forEach((edge) => {
    nodes.add(edge.gt)
    nodes.add(edge.lt)
  })
  equalities.forEach((equality) => {
    nodes.add(equality.a)
    nodes.add(equality.b)
  })
  Object.keys(seedIntervals).forEach((id) => nodes.add(id))

  // --- union-find over equalities, constants preferred as representatives ---
  const parent = new Map<VarId, VarId>()
  const mergeProvenance = new Map<VarId, Provenance[]>()
  const find = (id: VarId): VarId => {
    let root = id
    while (parent.get(root) !== undefined && parent.get(root) !== root) {
      root = parent.get(root) as VarId
    }
    let cursor = id
    while (parent.get(cursor) !== undefined && parent.get(cursor) !== cursor) {
      const next = parent.get(cursor) as VarId
      parent.set(cursor, root)
      cursor = next
    }
    return root
  }

  const fail = (conflict: GraphConflict): GraphResult => ({
    ok: false,
    conflict,
    order: [],
    intervals: {},
    entails: () => false,
  })

  for (const equality of equalities) {
    const rootA = find(equality.a)
    const rootB = find(equality.b)
    if (rootA === rootB) continue
    const constA = constantValue(rootA)
    const constB = constantValue(rootB)
    if (constA !== undefined && constB !== undefined && constA !== constB) {
      return fail({ kind: 'emptyInterval', provenance: [equality.provenance] })
    }
    const [root, child] = constA !== undefined ? [rootA, rootB] : [rootB, rootA]
    parent.set(child, root)
    mergeProvenance.set(root, [
      ...(mergeProvenance.get(root) ?? []),
      ...(mergeProvenance.get(child) ?? []),
      equality.provenance,
    ])
  }

  // --- re-target edges onto representatives; reject strict self-loops ---
  interface RepEdge {
    gt: VarId
    lt: VarId
    strict: boolean
    provenance: Provenance
  }
  const repEdges: RepEdge[] = []
  for (const edge of edges) {
    const gt = find(edge.gt)
    const lt = find(edge.lt)
    if (gt === lt) {
      if (edge.strict) {
        return fail({
          kind: 'selfLoop',
          provenance: [edge.provenance, ...(mergeProvenance.get(gt) ?? [])],
        })
      }
      continue // weak self-edge is vacuous
    }
    repEdges.push({ gt, lt, strict: edge.strict, provenance: edge.provenance })
  }

  const repNodes = new Set<VarId>()
  nodes.forEach((id) => repNodes.add(find(id)))

  // --- SCC detection (iterative Tarjan) over rep graph ---
  const adjacency = new Map<VarId, RepEdge[]>()
  repEdges.forEach((edge) => {
    const list = adjacency.get(edge.gt) ?? []
    list.push(edge)
    adjacency.set(edge.gt, list)
  })

  const indexOf = new Map<VarId, number>()
  const lowlink = new Map<VarId, number>()
  const onStack = new Set<VarId>()
  const stack: VarId[] = []
  const sccOf = new Map<VarId, number>()
  let index = 0
  let sccCount = 0

  for (const start of Array.from(repNodes)) {
    if (indexOf.has(start)) continue
    const work: Array<{ node: VarId; edgeIndex: number }> = [{ node: start, edgeIndex: 0 }]
    while (work.length > 0) {
      const frame = work[work.length - 1]
      const { node } = frame
      if (frame.edgeIndex === 0) {
        indexOf.set(node, index)
        lowlink.set(node, index)
        index += 1
        stack.push(node)
        onStack.add(node)
      }
      const outgoing = adjacency.get(node) ?? []
      let advanced = false
      while (frame.edgeIndex < outgoing.length) {
        const target = outgoing[frame.edgeIndex].lt
        frame.edgeIndex += 1
        if (!indexOf.has(target)) {
          work.push({ node: target, edgeIndex: 0 })
          advanced = true
          break
        }
        if (onStack.has(target)) {
          lowlink.set(node, Math.min(lowlink.get(node) as number, indexOf.get(target) as number))
        }
      }
      if (advanced) continue
      if ((lowlink.get(node) as number) === (indexOf.get(node) as number)) {
        for (;;) {
          const popped = stack.pop() as VarId
          onStack.delete(popped)
          sccOf.set(popped, sccCount)
          if (popped === node) break
        }
        sccCount += 1
      } else {
        work.pop()
        const parentFrame = work[work.length - 1]
        if (parentFrame) {
          lowlink.set(
            parentFrame.node,
            Math.min(lowlink.get(parentFrame.node) as number, lowlink.get(node) as number)
          )
        }
        continue
      }
      work.pop()
      const parentFrame = work[work.length - 1]
      if (parentFrame) {
        lowlink.set(
          parentFrame.node,
          Math.min(lowlink.get(parentFrame.node) as number, lowlink.get(node) as number)
        )
      }
    }
  }

  // Any strict edge inside an SCC is a genuine cycle contradiction.
  for (const edge of repEdges) {
    if (sccOf.get(edge.gt) === sccOf.get(edge.lt) && edge.strict) {
      const scc = sccOf.get(edge.gt)
      const provenance = repEdges
        .filter((member) => sccOf.get(member.gt) === scc && sccOf.get(member.lt) === scc)
        .map((member) => member.provenance)
      return fail({ kind: 'cycle', provenance })
    }
  }

  // --- topological order of SCCs (greatest-first) ---
  const sccAdjacency = new Map<number, Set<number>>()
  const inDegree = new Map<number, number>()
  repNodes.forEach((node) => {
    const scc = sccOf.get(node) as number
    if (!inDegree.has(scc)) inDegree.set(scc, 0)
  })
  repEdges.forEach((edge) => {
    const from = sccOf.get(edge.gt) as number
    const to = sccOf.get(edge.lt) as number
    if (from === to) return
    const targets = sccAdjacency.get(from) ?? new Set<number>()
    if (!targets.has(to)) {
      targets.add(to)
      sccAdjacency.set(from, targets)
      inDegree.set(to, (inDegree.get(to) ?? 0) + 1)
    }
  })
  const queue = Array.from(inDegree.entries())
    .filter(([, degree]) => degree === 0)
    .map(([scc]) => scc)
  const sccOrder: number[] = []
  while (queue.length > 0) {
    const scc = queue.shift() as number
    sccOrder.push(scc)
    ;(sccAdjacency.get(scc) ?? new Set<number>()).forEach((target) => {
      const remaining = (inDegree.get(target) as number) - 1
      inDegree.set(target, remaining)
      if (remaining === 0) queue.push(target)
    })
  }
  const sccPosition = new Map<number, number>()
  sccOrder.forEach((scc, position) => sccPosition.set(scc, position))
  const order = Array.from(nodes).sort(
    (a, b) =>
      (sccPosition.get(sccOf.get(find(a)) as number) ?? 0) -
      (sccPosition.get(sccOf.get(find(b)) as number) ?? 0)
  )

  // --- interval propagation ---
  // Bound provenances are chains: every update records the edge that caused it
  // plus the chain that produced the source bound, so a conflict surfacing on a
  // constant node still names every contributing condition.
  interface Bound {
    lo: number
    hi: number
    loProvenance: Provenance[]
    hiProvenance: Provenance[]
  }
  const bounds = new Map<VarId, Bound>()
  repNodes.forEach((node) => {
    const constant = constantValue(node)
    bounds.set(
      node,
      constant !== undefined
        ? { lo: constant, hi: constant, loProvenance: [], hiProvenance: [] }
        : { lo: -Infinity, hi: Infinity, loProvenance: [], hiProvenance: [] }
    )
  })
  for (const [id, seed] of Object.entries(seedIntervals)) {
    const bound = bounds.get(find(id)) as Bound
    bound.lo = Math.max(bound.lo, seed.lo)
    bound.hi = Math.min(bound.hi, seed.hi)
    if (bound.lo > bound.hi) {
      return fail({ kind: 'emptyInterval', provenance: [] })
    }
  }

  const maxPasses = repNodes.size + 1
  for (let pass = 0; pass < maxPasses; pass += 1) {
    let changed = false
    for (const edge of repEdges) {
      const margin = edge.strict ? EPS : 0
      const gtBound = bounds.get(edge.gt) as Bound
      const ltBound = bounds.get(edge.lt) as Bound
      const newLo = ltBound.lo + margin
      if (newLo > gtBound.lo) {
        gtBound.lo = newLo
        gtBound.loProvenance = [edge.provenance, ...ltBound.loProvenance]
        changed = true
        if (gtBound.lo > gtBound.hi) {
          return fail({
            kind: 'emptyInterval',
            provenance: [...gtBound.loProvenance, ...gtBound.hiProvenance],
          })
        }
      }
      const newHi = gtBound.hi - margin
      if (newHi < ltBound.hi) {
        ltBound.hi = newHi
        ltBound.hiProvenance = [edge.provenance, ...gtBound.hiProvenance]
        changed = true
        if (ltBound.lo > ltBound.hi) {
          return fail({
            kind: 'emptyInterval',
            provenance: [...ltBound.hiProvenance, ...ltBound.loProvenance],
          })
        }
      }
    }
    if (!changed) break
  }

  const intervals: Record<VarId, { lo: number; hi: number }> = {}
  nodes.forEach((id) => {
    const bound = bounds.get(find(id)) as Bound
    intervals[id] = { lo: bound.lo, hi: bound.hi }
  })

  // --- strict entailment via reachability (memoized DFS) ---
  const entailCache = new Map<string, boolean>()
  const entails = (gt: VarId, lt: VarId): boolean => {
    const from = find(gt)
    const to = find(lt)
    if (from === to) return false
    const cacheKey = `${from}>${to}`
    const cached = entailCache.get(cacheKey)
    if (cached !== undefined) return cached
    const visited = new Set<VarId>()
    const walk = (node: VarId, sawStrict: boolean): boolean => {
      if (node === to) return sawStrict
      const stateKey = `${node}|${sawStrict ? 1 : 0}`
      if (visited.has(stateKey)) return false
      visited.add(stateKey)
      for (const edge of adjacency.get(node) ?? []) {
        if (walk(edge.lt, sawStrict || edge.strict)) return true
      }
      return false
    }
    const result = walk(from, false)
    entailCache.set(cacheKey, result)
    return result
  }

  return { ok: true, order, intervals, entails }
}
